import React, { useState, useEffect, useContext } from "react";
import { Box, Chip, Container, IconButton, LinearProgress, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { LineChart } from "@mui/x-charts/LineChart";
import { useParams } from "react-router-dom";
import moment from "moment";
import bkstApi from "../../api";
import UserAvatar from "../../common/UserAvatar";
import StockUpdateDialog from "../component/StockUpdateDialog";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { UserContext } from "../../context/UserContext";

const TYPE_LABELS = {
  production: { label: "Production", color: "success" },
  add: { label: "Added", color: "success" },
  sale: { label: "Sale", color: "primary" },
  sale_adjustment: { label: "Sale Adjustment", color: "primary" },
  spoilage: { label: "Spoilage", color: "error" },
  breakage: { label: "Breakage", color: "error" },
  shrinkage: { label: "Shrinkage", color: "error" },
  adjustment: { label: "Adjustment", color: "default" },
};

const columns = [
  {
    field: "ts",
    headerName: "Timestamp",
    width: 120,
    renderCell: (params) => (
      <Box>
        <Typography variant="body2">{moment(params.value).format("MMM D, YYYY")}</Typography>
        <Typography variant="caption" color="textSecondary">
          {moment(params.value).format("h:mm A")}
        </Typography>
      </Box>
    ),
  },
  {
    field: "type",
    headerName: "Activity",
    width: 140,
    renderCell: (params) => {
      const typeInfo = TYPE_LABELS[params.value] || { label: params.value, color: "default" };
      return <Chip label={typeInfo.label.toLowerCase()} color={typeInfo.color} size="small" />;
    },
  },
  {
    field: "quantity",
    headerName: "Change",
    width: 100,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      const qty = params.value;
      const isPositive = qty > 0;
      return <Typography variant="body1">{isPositive ? `+${qty}` : qty}</Typography>;
    },
  },
  {
    field: "balance",
    headerName: "In-Stock",
    width: 100,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => (
      <Typography
        variant="body1"
        sx={{ color: params.value < 0 ? "error.main" : "inherit", fontWeight: 600 }}
      >
        {params.value}
      </Typography>
    ),
  },
  {
    field: "uid",
    headerName: "User",
    width: 80,
    renderCell: (params) => {
      if (params.value) {
        return <UserAvatar uid={params.value} />;
      }
      return null;
    },
  },
];

export default function ProductInventory(props) {
  const { productId } = useParams();
  const { user } = useContext(UserContext);

  const [product, setProduct] = useState(null);
  const [trail, setTrail] = useState([]);
  const [filteredTrail, setFilteredTrail] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedActivityFilter, setSelectedActivityFilter] = useState(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState("add");

  useEffect(() => {
    search(moment().subtract(30, "days").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD"));
  }, [productId]);

  const search = (from, to) => {
    setLoading(true);
    bkstApi(`/my/product/${productId}/inventory?from=${from}&to=${to}`)
      .then((res) => {
        setProduct(res.data.product);
        // Flatten activity arrays and calculate running balance
        const trail = res.data.trail || [];
        const flattenedTrail = [];
        let runningBalance = 0;

        trail.forEach((period) => {
          // Set opening balance for this period if available
          if (period.openingStock !== undefined) {
            runningBalance = period.openingStock;
          } else if (period.openingInventory !== undefined) {
            runningBalance = period.openingInventory;
          }

          // Process each activity in this period
          (period.activity || []).forEach((item) => {
            runningBalance += item.quantity;
            flattenedTrail.push({
              ...item,
              balance: runningBalance,
            });
          });
        });

        // Sort by timestamp descending (most recent first)
        flattenedTrail.sort((a, b) => b.ts - a.ts);

        setTrail(flattenedTrail);
        setFilteredTrail(flattenedTrail);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching inventory trail:", error);
        setTrail([]);
        setLoading(false);
      });
  };

  const handleOpenDialog = (action) => {
    setSelectedAction(action);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleStockUpdate = (productId, newStock, activity) => {
    setProduct((prev) => ({ ...prev, stock: newStock }));
    // Add new entry to trail
    const newEntry = {
      ts: Date.now(),
      type: activity.type,
      quantity: activity.quantity,
      balance: newStock,
      uid: user.uid,
    };
    setTrail((prev) => [newEntry, ...prev]);
    // Update filtered trail if no filter is active, or if it matches the filter
    if (!selectedActivityFilter || selectedActivityFilter === activity.type) {
      setFilteredTrail((prev) => [newEntry, ...prev]);
    }
  };

  // Filter trail by activity type
  useEffect(() => {
    if (selectedActivityFilter) {
      setFilteredTrail(trail.filter((item) => item.type === selectedActivityFilter));
    } else {
      setFilteredTrail(trail);
    }
  }, [trail, selectedActivityFilter]);

  // Get unique activity types from trail
  const getAvailableActivityTypes = () => {
    const types = new Set(trail.map((item) => item.type));
    return Array.from(types).filter((type) => TYPE_LABELS[type]);
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box>
      <Container maxWidth="md">
        <Box mt={6}>
          <Box mb={4}>
            <Typography variant="h1" gutterBottom>
              Inventory | {product?.title}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Track stock changes from production, sales, and losses over time.
            </Typography>
          </Box>

          <Box
            mb={3}
            p={2}
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              border: "1px solid #ECECEC",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="h2" color={product?.stock < 0 ? "error" : "inherit"}>
                {product?.stock ?? 0}
              </Typography>
              <Typography variant="overline" color="textSecondary">
                In Stock
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                color="success"
                onClick={() => handleOpenDialog("add")}
                title="Log Production"
                size="large"
                sx={{ border: "2px solid", borderColor: "success.main" }}
              >
                <AddIcon />
              </IconButton>
              <IconButton
                color="error"
                onClick={() => handleOpenDialog("remove")}
                title="Log Loss"
                size="large"
                disabled={!product?.stock || product.stock <= 0}
                sx={{ border: "2px solid", borderColor: "error.main" }}
              >
                <RemoveIcon />
              </IconButton>
            </Box>
          </Box>

          {trail.length > 1 && (
            <Box mb={3} sx={{ backgroundColor: "#ECECEC", borderRadius: "4px" }}>
              <LineChart
                dataset={[...trail]
                  .sort((a, b) => a.ts - b.ts)
                  .map((item) => ({
                    date: new Date(item.ts),
                    stock: item.balance,
                  }))}
                xAxis={[
                  {
                    scaleType: "time",
                    dataKey: "date",
                    valueFormatter: (value) => moment(value).format("MMM D"),
                  },
                ]}
                series={[
                  {
                    dataKey: "stock",
                    color: "#78374a",
                    label: "Stock",
                    showMark: ({ index }) => index % 2 === 0,
                  },
                ]}
                height={250}
              />
            </Box>
          )}

          {trail.length > 0 ? (
            <Box mt={4} sx={{ width: "100%" }}>
              <Typography variant="h2" gutterBottom>
                Activity <small>(last 30 days)</small>
              </Typography>
              {/* Quick Activity Filters */}
              <Box mb={2} sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Chip
                  label="all"
                  size="small"
                  onClick={() => setSelectedActivityFilter(null)}
                  color={selectedActivityFilter === null ? "primary" : "default"}
                  variant={selectedActivityFilter === null ? "filled" : "outlined"}
                  clickable
                />
                {getAvailableActivityTypes().map((type) => {
                  const typeInfo = TYPE_LABELS[type] || { label: type, color: "default" };
                  return (
                    <Chip
                      key={type}
                      label={typeInfo.label.toLowerCase()}
                      size="small"
                      onClick={() =>
                        setSelectedActivityFilter(selectedActivityFilter === type ? null : type)
                      }
                      color={selectedActivityFilter === type ? "primary" : "default"}
                      variant={selectedActivityFilter === type ? "filled" : "outlined"}
                      clickable
                    />
                  );
                })}
              </Box>

              <DataGrid
                rows={filteredTrail.map((item, index) => ({
                  id: item.id || `${item.ts}-${index}`,
                  ...item,
                }))}
                columns={columns}
                autoHeight
                disableRowSelectionOnClick
                disableColumnMenu
                hideFooter={filteredTrail.length <= 100}
                sx={{
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #ECECEC",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#ECECEC",
                  },
                  "& .MuiDataGrid-row:nth-of-type(odd)": {
                    backgroundColor: "#FAFAFA",
                  },
                  "& .MuiDataGrid-row:nth-of-type(even)": {
                    backgroundColor: "#FFF",
                  },
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                }}
              />
            </Box>
          ) : (
            <Box py={4} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                No inventory trail found.
              </Typography>
            </Box>
          )}
        </Box>
      </Container>

      <StockUpdateDialog
        open={dialogOpen}
        product={product}
        action={selectedAction}
        onClose={handleCloseDialog}
        onStockUpdate={handleStockUpdate}
      />
    </Box>
  );
}
